import {React, useContext, useMemo, DatePicker, CustomDateInput, registerDatePickerLocale} from 'external/react'
import {styled, clsx, Box, Button, InputAdornment} from 'external/material'

import useForm from 'helpers/form-hook'
import Dialog from 'components/responsive-fullscreen-dialog'

import useValidations from 'helpers/validations-hook'
import {createPost} from 'helpers/command'
import { getSelectedPeriod } from 'helpers/gradebook'
import {requiredTextAfterAttempt} from 'helpers/validations'
import { parseDate, locales } from 'external/date-fns'

import TextInput from './TextInput'
import Select from './Select'


import {TeacherContext} from 'teacher/context'

const initialState = {
  attempted: false,
  type: '',
  title: '',
  value: '',
  date: ''
}

const validations = {
  title: [requiredTextAfterAttempt('title', 'required-external-grade-name')],
  value: [requiredTextAfterAttempt('value', 'required-external-grade-value')],
  type: [({attempted, type}: any) => {
    return (!attempted || type != '') ? '' : 'required-external-grade-type'
  }]
}

Object.entries(locales).forEach(([languages, locale]) => registerDatePickerLocale(languages, locale))

const StyledForm = styled(Dialog)(formStyles)

export default function AddExternalGradeForm (props: any) {


  const context = useContext(TeacherContext) as any
  const {query, groupId, gradingCategories, translate, updateState, rollbookContextGradingDialogs: gradingDialogs, languageId} = context as any
  const {items: periods} = context.rollbookContextPeriods
  
  const [periodName, periodStart, periodFinish] = useMemo(() => {
    const period = getSelectedPeriod(periods, query.periodId)
    if (!period) return ['', false, false]
    return [period.properties.name, parseDate(period.start), parseDate(period.finish)]

  },[periods, query.periodId])

  
  const isOpen = gradingDialogs['add-external-grade']
  
  const {state, setState, handleChange, resetState} = useForm(initialState)
  const {errors, validate, resetErrors} = useValidations(validations)
  
  function closeDialog (refresh: any) {
    const command = refresh
      ? {rollbookContextGradingDialogs: {'add-external-grade': {$set: false}}, rollbookContextGradeColumns: {refreshes: {$apply: (i: any) => i + 1}}}
      : {rollbookContextGradingDialogs: {'add-external-grade': {$set: false}}}
    updateState(command)
    resetState()
    resetErrors()
  }
  
  async function addExternalGrade() {
    if (validate({...state, attempted: true})) {
      try {
        const [created] = await createPost('grading','addExternalGrade')({clique: groupId, title: state.title, category: state.type, value: parseInt(state.value), date: state.date})
        if (created) {
          closeDialog(true)
        } else {
          setState({...state, attempted: true})
        }
      } catch (e) {
      }
    }
    else {
      setState({...state, attempted: true})
    }
  }
  const assignDate = (newDate: Date) => {
    setState({...state, date: newDate})
  }
  
  const getValueErrorMessage = () => {
    if (state.value && (parseInt(state.value) < 0)) return translate('value-not-in-range', {min: 0})
    return errors.value.length > 0 ? translate(errors.value) : ''
  }
  
  React.useEffect(() => { validate(state) }, [state])
  return (
    <StyledForm
      open={isOpen}
      close={closeDialog}
      actionsClassName="actions"
      confirm={
        <Button
          variant="contained"
          className={clsx("button", "create")}
          onClick={addExternalGrade}>
            {translate('add-grade')}
        </Button>
      }
      cancel={
        <Button
          variant="contained"
          className={clsx("button", "cancel")}
          onClick={() => closeDialog(false)}>
            {translate('cancel')}
        </Button>
      }
      title={translate('add-external-grade-dialog-title')}
      subtitle={periodName}
    >
        <Box className={"root"}>
          <Box className={clsx("field", "title")}>
           <TextInput
             controlFullWidth
             controlVariant="outlined"
             customClassName='box-field-input'
             controlMargin="dense"
             inputPlaceholder='Ej. Proyecto final'
             id="title"
             inputAutoComplete="title"
             labelRequired
             inputAutoFocus
             caption={translate('title')}
             value={state.title}
             inputOnChange={handleChange('title')}
             controlError={errors.title.length > 0}
             errorMessage={errors.title.length > 0 ? translate(errors.title) : undefined}
           />
          </Box>
          <Box className={clsx("field", "type")}>
            <Select
              controlFullWidth
              controlVariant="outlined"
              customClassName='box-field-input'
              controlMargin="dense"
              id="type"
              labelRequired
              caption={translate('type_label')}
              selectValue={state.type}
              selectOnChange={handleChange('type', e => parseInt(e.target.value))}
              controlError={errors.type.length > 0}
              errorMessage={errors.type.length > 0 ? translate(errors.type): undefined}
              mapValue={(o: any) => o.id}
              mapText={(o: any) => translate(`${o.name}-label`)}
              options={gradingCategories}
            />
          </Box>
          <Box className={clsx("field", "value")}>
            <TextInput
              controlFullWidth
              customClassName='box-field-input'
              controlVariant="outlined"
              controlMargin="dense"
              inputPlaceholder='0'
              inputendAdornment={<InputAdornment position="end">pts</InputAdornment>}
              labelRequired
              id="value"
              inputAutoComplete="value"
              caption={translate('value')}
              value={state.value}
              inputOnChange={(e: any) => {
                if(isNaN((e.target.value))) return
                handleChange('value')(e)
              }}
              controlError={errors.value.length > 0 || getValueErrorMessage() !== ''}
              errorMessage={getValueErrorMessage()}
            />
          </Box>
          <Box className={clsx("field", "date")}>
            <Box className="label">{translate('date')}</Box>
            <DatePicker
              customInput={<CustomDateInput />}
              selected={state.date || periodStart}
              onChange={assignDate}
              minDate={periodStart}
              maxDate={periodFinish}
              locale={languageId}
              popperPlacement="auto-start"
            />
          </Box>
        </Box>
    </StyledForm>
  )
}

function formStyles() {
  return {
    '& .MuiDialog-paper': {
      padding: '24px',
      width: '410px',
      boxSizing: 'border-box'
    },
    '& .MuiDialogContent-root': {
      marginTop: '18px',
      padding: 0
    },
    '& .field.date': {
      gap: '4px',
      display: 'flex',
      flexDirection: 'column'
    },
    '& .MuiDialogActions-root': {
      gap: '8px',
      marginTop: '40px',
      padding: 0
    },
    '& .root': {
      gap: '12px',
      display: 'flex',
      flexDirection: 'column'
    },
    '& .MuiInputBase-input': {
      height: '35px',
      boxSizing: 'border-box'
    },
    '& .MuiSelect-select': {
      height: '35px', 
      boxSizing: 'border-box',
      padding: '6px 10px',
    },
    '& .label': {
      color: '#374151',
      fontSize: '14px',
      fontWeight: 500
    }, 
    '& .box-field-input': {
      gap: '4px',
      display: 'flex',
      flexDirection: 'column'
    },
    '& .MuiTypography-root': {
      padding: 0
    },
    '& .button': {
      boxShadow: 'none',
      textTransform: 'none'
    },
    '& .cancel':{
      backgroundColor: 'white',
      color: 'black',
      border: '1px solid #a7aaaf'
    },
    "& .date-input": {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      cursor: 'pointer',
      paddingLeft: '10px',
      border: 'solid 1px #b8c1c7',
      height: '35px',
      boxSizing: 'border-box',
      borderRadius: '4px'
    }
    // '& .field': {
    //   '& .title, .type, .value': {
    //     backgroundColor: 'red'
    //   } 
    // }
  }
  // const verticalCentering = {
  //   display: 'flex',
  //   flexDirection: 'column',
  //   justifyContent: 'center'
  // }

  // return {
  //   "& .date-input": {
  //     display: 'flex',
  //     flexDirection: 'column',
  //     justifyContent: 'center',
  //     cursor: 'pointer',
  //     width: '110px',
  //     padding: 0,
  //     textAlign: 'center',
  //     border: 'solid 1px #b8c1c7',
  //     height: '28px'
  //   },
  //   "& .root": {
  //     display: 'flex',
  //     flexDirection: 'column',
  //   },
  //   "& .field": {
  //     flexGrow: 1,
  //     display: 'flex',
  //     marginBottom: 25,
  //     minWidth: 500,
  //     "& > div": {
  //       display: 'flex',
  //       flexGrow: 1,
  //       "& .label": {
  //         width: 90,
  //         fontSize: '12pt',
  //         fontWeight: 'bold',
  //         color: '#878ea1',
  //         ...verticalCentering
  //       },
  //       "& .input": {
  //         flexGrow: 1,
  //         ...verticalCentering,
  //         "& input": {
  //           borderRadius: 4,
  //           border: 'solid 1px #aab7bf !important',
  //         },
  //         "& .select": {
  //           "& .MuiInput-underline:before": {
  //             content: 'none',
  //             border: 'none'
  //           },
  //           "& .MuiSelect-select": {
  //             border: 'solid 1px #aab7bf',
  //             borderRadius: 4,
  //             padding: 10,
  //             paddingRight: 24
  //           },
  //         }
  //       }
  //     }
  //   },
  //   "& .button": {
  //     width: 150,
  //     fontSize: "13pt",
  //     textTransform: "none",
  //     boxShadow: 'none',
  //     borderRadius: 4,
  //     "&:first-of-type": {
  //       marginRight: 10
  //     },
  //     "&:last-child": {
  //       marginLeft: 10
  //     },
  //     "&.cancel": {
  //       border: "solid 1px #3d4556",
  //       backgroundColor: '#fff',
  //       color: "#3d4556",
  //       '&:hover': {
  //         color: "#000",
  //         border: "solid 1px #000",
  //         backgroundColor: '#eaf5ff',
  //       },
  //     },
  //     "&.create" : {
  //       color: "#fff",
  //       border: 'solid 1px #1d9be5',
  //       backgroundColor: '#1d9be5',
  //       '&:hover': {
  //         backgroundColor: '#29739f',
  //         borderColor: '#29739f',
  //         boxShadow: 'none',
  //       },
  //     }
  //   },
  //   "& .actions": {
  //     padding: '8px 24px 30px 24px',
  //   }
  // }
}
